import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPiggyBank, FaCalendarAlt, FaMoneyBillWave, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const AtalPensionYojanaCalculator = () => {
  const [formData, setFormData] = useState({
    age: '',
    monthlyContribution: '',
    pensionAmount: '',
    joiningAge: '',
    retirementAge: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculatePension = () => {
    const { age, monthlyContribution, pensionAmount, joiningAge, retirementAge } = formData;

    if (!age || !monthlyContribution || !pensionAmount || !joiningAge || !retirementAge) {
      setError('Please fill in all required fields.');
      return;
    }

    const contributionYears = retirementAge - joiningAge;
    const totalContribution = parseFloat(monthlyContribution) * 12 * contributionYears;
    const monthlyPension = parseFloat(pensionAmount);

    // Calculate yearly contributions and pension growth
    const yearlyData = [];
    let totalContributed = 0;
    let totalPension = 0;

    for (let year = joiningAge; year <= 100; year++) {
      if (year <= retirementAge) {
        totalContributed += parseFloat(monthlyContribution) * 12;
      } else {
        totalPension += monthlyPension * 12;
      }
      yearlyData.push({
        year,
        contributions: totalContributed,
        pension: totalPension
      });
    }

    const results = {
      contributionYears,
      totalContribution,
      monthlyPension,
      annualPension: monthlyPension * 12,
      totalPension: monthlyPension * 12 * (100 - retirementAge),
      yearlyData
    };

    setResults(results);
    setError('');
  };

  const getChartOptions = () => {
    if (!results?.yearlyData) return {};

    const years = results.yearlyData.map(data => data.year);
    const contributions = results.yearlyData.map(data => data.contributions);
    const pension = results.yearlyData.map(data => data.pension);

    // Find maximum values for better scaling
    const maxContributions = Math.max(...contributions);
    const maxPension = Math.max(...pension);
    const maxValue = Math.max(maxContributions, maxPension);

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        width: [3, 3],
        curve: 'smooth'
      },
      xaxis: {
        categories: years,
        title: {
          text: 'Age',
          style: {
            color: '#333',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px'
          },
          rotate: -45
        }
      },
      yaxis: {
        title: {
          text: 'Amount (₹)',
          style: {
            color: '#333',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px'
          },
          formatter: function (val) {
            // Format numbers with commas and limit decimal places
            return '₹' + val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          },
          min: 0,
          max: maxValue * 1.1, // Add 10% padding
          tickAmount: 5
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        fontSize: '14px',
        markers: {
          width: 12,
          height: 12
        }
      },
      colors: ['#4F46E5', '#10B981'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100]
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return '₹' + val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        },
        marker: {
          size: 8
        }
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
        strokeDashArray: 0,
        position: 'back'
      }
    };
  };

  return (
    <div className="calculator-container bg-gradient-to-r from-blue-500 to-cyan-500 p-4 text-white">
      <Helmet>
        <title>Atal Pension Yojana Calculator</title>
        <meta name="description" content="Calculate your pension under the Atal Pension Yojana scheme." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaPiggyBank className="calculator-icon text-4xl text-white" />
        <h1 className="text-3xl font-bold text-yellow-500 mb-2">Atal Pension Yojana Calculator</h1>
        <p className="text-gray-300">Calculate your pension under the APY scheme</p>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={(e) => e.preventDefault()} className="calculator-form space-y-6">
            <div className="input-group">
              <label htmlFor="age" className="block text-gray-700 font-bold mb-2">
                <FaCalendarAlt className="inline-block mr-2 text-gray-700" /> Current Age
              </label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                placeholder="Enter your current age"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="monthlyContribution" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2 text-gray-700" /> Monthly Contribution
              </label>
              <input
                type="number"
                id="monthlyContribution"
                name="monthlyContribution"
                value={formData.monthlyContribution}
                onChange={handleChange}
                placeholder="Enter monthly contribution"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="pensionAmount" className="block text-gray-700 font-bold mb-2">
                <FaChartLine className="inline-block mr-2 text-gray-700" /> Desired Monthly Pension
              </label>
              <input
                type="number"
                id="pensionAmount"
                name="pensionAmount"
                value={formData.pensionAmount}
                onChange={handleChange}
                placeholder="Enter desired pension amount"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="joiningAge" className="block text-gray-700 font-bold mb-2">
                <FaCalendarAlt className="inline-block mr-2 text-gray-700" /> Joining Age
              </label>
              <input
                type="number"
                id="joiningAge"
                name="joiningAge"
                value={formData.joiningAge}
                onChange={handleChange}
                placeholder="Enter joining age"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="retirementAge" className="block text-gray-700 font-bold mb-2">
                <FaCalendarAlt className="inline-block mr-2 text-gray-700" /> Retirement Age
              </label>
              <input
                type="number"
                id="retirementAge"
                name="retirementAge"
                value={formData.retirementAge}
                onChange={handleChange}
                placeholder="Enter retirement age"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <button
              onClick={calculatePension}
              className="calculate-button w-full bg-blue-500 text-white rounded-md py-3 px-6 font-semibold hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200"
            >
              Calculate Pension
            </button>

            {error && (
              <div className="error-message bg-red-50 text-red-600 rounded-md p-3 mt-4">
                {error}
              </div>
            )}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Pension Summary</h2>
              <FaChartLine className="results-icon text-3xl text-gray-900" />
            </div>

            <div className="results-grid grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Contribution Years</h3>
                    <p className="text-gray-600">Years of contribution</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    {results.contributionYears}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Total Contribution</h3>
                    <p className="text-gray-600">Total amount contributed</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.totalContribution.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Monthly Pension</h3>
                    <p className="text-gray-600">Monthly pension amount</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.monthlyPension.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Annual Pension</h3>
                    <p className="text-gray-600">Annual pension amount</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.annualPension.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Total Pension</h3>
                    <p className="text-gray-600">Total pension amount</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.totalPension.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {results?.yearlyData && (
              <div className="chart-section mt-8 bg-white text-black bg-opacity-90 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Pension Growth Over Time</h2>
                <Chart
                  options={getChartOptions()}
                  series={[
                    {
                      name: 'Contributions',
                      data: results.yearlyData.map(data => data.contributions)
                    },
                    {
                      name: 'Pension',
                      data: results.yearlyData.map(data => data.pension)
                    }
                  ]}
                  type="line"
                  height={400}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaPiggyBank className="feature-icon text-3xl text-yellow-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Government Guarantee</h3>
            <p className="text-gray-700">Your pension is guaranteed by the government.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaMoneyBillWave className="feature-icon text-3xl text-green-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Fixed Pension</h3>
            <p className="text-gray-700">Receive a fixed monthly pension after retirement.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaChartLine className="feature-icon text-3xl text-blue-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Calculation</h3>
            <p className="text-gray-700">Calculate your pension amount easily.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5 space-y-3">
          <li className="text-gray-700">Enter your current age</li>
          <li className="text-gray-700">Enter your monthly contribution amount</li>
          <li className="text-gray-700">Enter your desired monthly pension amount</li>
          <li className="text-gray-700">Enter your joining age</li>
          <li className="text-gray-700">Enter your retirement age</li>
          <li className="text-gray-700">Click "Calculate Pension" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is Atal Pension Yojana?</h3>
            <p className="text-gray-700">APY is a government pension scheme that provides a fixed monthly pension after retirement.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Who can join APY?</h3>
            <p className="text-gray-700">Indian citizens aged 18-40 years can join the scheme.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is the minimum contribution?</h3>
            <p className="text-gray-700">The minimum monthly contribution varies based on the desired pension amount.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is the pension amount?</h3>
            <p className="text-gray-700">You can choose a pension amount of ₹1,000, ₹2,000, ₹3,000, ₹4,000, or ₹5,000 per month.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtalPensionYojanaCalculator;
